import React, { useState, useMemo } from "react";
import {
  VscDash,
  VscListSelection
} from "react-icons/vsc";
import {
  MdOutlineLocationOn,
  MdOutlineEvent,
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineEdit,
  MdLink
} from "react-icons/md";
import { BsPersonCircle } from "react-icons/bs";
import { SiGooglemeet } from "react-icons/si";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";

// --- ReactQuill Config ---
const modules = {
  toolbar: [
    [{ font: [] }, { size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
    ["link", "image"],
    ["clean"],
  ],
};

const formats = [
  "font", "size", "bold", "italic", "underline", "strike", "color", "background",
  "list", "bullet", "align", "blockquote", "link", "image", "clean",
];

// Helper to extract an email from various possible attendee formats
const getAttendeeEmail = (attendee) => {
  // Handles the nested structure from Outlook/Graph API
  if (typeof attendee === 'object' && attendee !== null && attendee.emailAddress) {
    return attendee.emailAddress.address || attendee.emailAddress.name || '';
  }
  // Handles Google's format { email: '...' }
  if (typeof attendee === 'object' && attendee !== null && attendee.email) {
    return attendee.email;
  }
  // Handles if the attendee is just a string
  if (typeof attendee === 'string') {
    return attendee;
  }
  // Fallback for any other format
  return '';
};

// --- Main Component (Corrected for Hooks Rule) ---
export default function CalendarEventEditor({
  event = {},
  onUpdate,
  approvalNeeded = false,
}) {
  const [isEditable, setIsEditable] = useState(false);
  const [originalEventOnEdit, setOriginalEventOnEdit] = useState(null);
  const [attendeeInputValue, setAttendeeInputValue] = useState("");

  // --- HOOK FIX: Moved useMemo to the top level ---
  // It now safely handles a null 'event' object.
  const normalizedAttendees = useMemo(() => {
    if (!event || !Array.isArray(event.attendees)) {
      return [];
    }
    // Map to email strings and filter out any empty results from bad data
    return event.attendees.map(getAttendeeEmail).filter(Boolean);
  }, [event]); // Depend on the 'event' object itself

  // This conditional return is now AFTER all hook calls.
  if (!event) return null;

  // Helper to safely get the location display name
  const getLocationDisplayName = (location) => {
    if (typeof location === 'object' && location !== null && location.displayName) {
      return location.displayName;
    }
    return location || ''; // Fallback for string or empty values
  };

  const updateEventField = (field, value) => {
    if (onUpdate) {
      onUpdate({ ...event, [field]: value });
    }
  };

  const handleInputChange = (e) => {
    updateEventField(e.target.name, e.target.value);
  };

  const handleDescriptionChange = (content) => {
    updateEventField("description", content);
  };

  const handleAttendeeKeyDown = (e) => {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      const email = attendeeInputValue.trim().replace(/,$/, "");
      
      // Check for duplicates against the already-normalized list
      if (email && !normalizedAttendees.includes(email)) {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          // Add the new email (as a string) to the original attendees array
          const newAttendees = [...(event.attendees || []), email];
          updateEventField("attendees", newAttendees);
          setAttendeeInputValue("");
        } else {
          toast.error("Invalid email format!");
        }
      } else if (email) {
         setAttendeeInputValue(""); // Clear input if it's a duplicate
      }
    }
  };

  const removeAttendee = (indexToRemove) => {
    // This logic correctly removes from the original array by index
    const newAttendees = (event.attendees || []).filter((_, index) => index !== indexToRemove);
    updateEventField("attendees", newAttendees);
  };

  const handleEditClick = () => {
    setOriginalEventOnEdit(event);
    setIsEditable(true);
  };

  const handleCancel = () => {
    if (onUpdate) {
      onUpdate(originalEventOnEdit);
    }
    setIsEditable(false);
    setOriginalEventOnEdit(null);
  };

  const handleSave = () => {
    setIsEditable(false);
    setOriginalEventOnEdit(null);
    toast.success("Event changes are staged for approval.");
  };

  return (
    <div className="flex flex-col items-start justify-start w-full gap-2 pt-0 md:flex-row">
      <div className="flex flex-col w-full gap-4 p-4 mb-4 text-gray-700 bg-white shadow-lg rounded-2xl">
        {/* Title Section */}
        <div className="flex flex-col items-start justify-between w-full gap-2 sm:flex-row sm:items-center text-md">
          <div className="relative flex items-center w-full">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineEvent className="text-gray-400" size={20} /></div>
            {isEditable ? (
              <input type="text" name="title" value={event.title || ''} onChange={handleInputChange} className="w-full p-1 pl-10 font-bold border border-gray-300 rounded-lg text-md focus:outline-none focus:ring-2 focus:ring-[#5652CF]" />
            ) : (
              <h2 className="w-full pl-10 font-bold break-words text-md">{event.title}</h2>
            )}
          </div>
          <div className="flex items-center w-full gap-2 sm:w-auto">
            {approvalNeeded && !isEditable && (
              <div className="flex items-center justify-center px-2 py-2 transition-all duration-200 rounded-full hover:bg-gray-200">
                <MdOutlineEdit size={20} className="cursor-pointer" onClick={handleEditClick} />
              </div>
            )}
            <div className="px-3 py-1 text-sm font-semibold text-white bg-[#5652CF] rounded-md">Event</div>
          </div>
        </div>

        {/* Date and Time Section */}
        <div className="flex flex-wrap items-center gap-2 pb-2 text-sm border-b border-gray-200">
          {event.date && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineEvent className="text-gray-400" size={20} /></div>
                {isEditable ? (
                  <input type="date" name="date" value={event.date || ''} onChange={handleInputChange} className="w-full px-3 py-1 pl-10 text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5652CF] focus:outline-none" />
                ) : (
                  <span className="pl-10 text-gray-700">{event.date}</span>
                )}
              </div>
              <VscDash />
            </div>
          )}
          {(isEditable || event.endDate) && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineEvent className="text-gray-400" size={20} /></div>
                {isEditable ? (
                  <input type="date" name="endDate" value={event.endDate || event.date || ''} onChange={handleInputChange} className="w-full px-3 py-1 pl-10 text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5652CF] focus:outline-none" />
                ) : (
                  <span className="pl-10 text-gray-700">{event.endDate}</span>
                )}
              </div>
              <VscDash />
            </div>
          )}
          {event.startTime && (
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineAccessTime className="text-gray-400" size={20} /></div>
                {isEditable ? (
                  <input type="time" name="startTime" value={event.startTime || ''} onChange={handleInputChange} className="w-full px-3 py-1 pl-10 text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5652CF] focus:outline-none" />
                ) : (
                  <span className="pl-10 text-gray-700">{event.startTime}</span>
                )}
              </div>
              <VscDash />
            </div>
          )}
          {event.endTime && (
            <div className="relative flex items-center">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineAccessTime className="text-gray-400" size={20} /></div>
              {isEditable ? (
                <input type="time" name="endTime" value={event.endTime || ''} onChange={handleInputChange} className="w-full px-3 py-1 pl-10 text-gray-700 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5652CF] focus:outline-none" />
              ) : (
                <span className="pl-10 text-gray-700">{event.endTime}</span>
              )}
            </div>
          )}
        </div>

        {/* Details Section */}
        <div className="flex flex-col w-full gap-4 pt-2 lg:flex-row">
          <div className="flex flex-col w-full gap-4 text-sm">
            {(isEditable || (event.location && getLocationDisplayName(event.location))) && (
              <div className="relative flex items-center w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlineLocationOn className="text-gray-400" size={20} /></div>
                {isEditable ? (
                  <input type="text" name="location" value={getLocationDisplayName(event.location)} onChange={handleInputChange} className="w-full p-1 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5652CF]" placeholder="Add a location..." />
                ) : (
                  <span className="pl-10 text-gray-700 break-words">{getLocationDisplayName(event.location)}</span>
                )}
              </div>
            )}
            {isEditable ? (
              <div className="relative flex items-center w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdLink className="text-gray-400" size={20} /></div>
                <input type="text" name="meetLink" value={event.meetLink || ''} onChange={handleInputChange} className="w-full p-1 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5652CF]" placeholder="Add a meeting link..." />
              </div>
            ) : event.meetLink ? (
              <div className="relative flex items-center w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdLink className="text-gray-400" size={20} /></div>
                <a href={event.meetLink} target="_blank" rel="noopener noreferrer" className="pl-10 text-blue-600 break-words hover:underline">{event.meetLink}</a>
              </div>
            ) : (
              <div className="flex items-center gap-3 pl-3">
                <SiGooglemeet className="w-5 h-5 text-gray-400" />
                <div className="flex flex-col">
                  <div className="px-4 py-1 font-semibold text-white bg-[#5652CF] rounded-lg w-fit">Awaiting Approval</div>
                  <span className="mt-1 text-xs text-gray-500">Meeting link will be generated</span>
                </div>
              </div>
            )}
            {(isEditable || event.organizer) && (
              <div className="relative flex items-center w-full">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3"><MdOutlinePerson className="text-gray-400" size={20} /></div>
                {isEditable ? (
                  <input type="text" name="organizer" value={event.organizer || ''} onChange={handleInputChange} className="w-full p-1 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5652CF]" placeholder="Organizer..." />
                ) : (
                  <div className="flex items-center gap-2 pl-10">
                    <span className="text-gray-700">Created by</span>
                    <div className="flex items-center gap-2 px-2 py-1 bg-zinc-300 rounded-xl">
                      <BsPersonCircle className="w-4 h-4 text-zinc-600" />
                      <span className="text-xs font-medium text-zinc-600">{event.organizer}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        {(isEditable || event.description) && (
          <div className="flex items-start gap-4">
            <div className="w-6 px-3"><VscListSelection size={20} className="text-gray-400" /></div>
            {isEditable ? (
              <ReactQuill
                value={event.description || ''}
                onChange={handleDescriptionChange}
                modules={modules}
                formats={formats}
                className="w-full text-xs text-gray-600"
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: event.description }} className="w-full text-sm break-words quill-read-only" />
            )}
          </div>
        )}

        {/* --- Attendees Section (CLEANED UP) --- */}
        {(normalizedAttendees.length > 0 || isEditable) && (
          <div className="flex flex-col gap-2">
            <div className="font-semibold">Attendees</div>
            {isEditable ? (
              <div className="flex flex-wrap items-center w-full gap-1 p-1 text-xs border rounded-lg border-zinc-300">
                {normalizedAttendees.map((email, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-1 text-gray-600 rounded-lg bg-zinc-300">
                    {email}
                    <button onClick={() => removeAttendee(index)} className="ml-2 text-gray-600 hover:text-red-400">&times;</button>
                  </span>
                ))}
                <input
                  type="text"
                  className="bg-transparent outline-none flex-grow text-gray-600 ml-1 min-w-[100px]"
                  placeholder="Add attendee email..."
                  value={attendeeInputValue}
                  onChange={(e) => setAttendeeInputValue(e.target.value)}
                  onKeyDown={handleAttendeeKeyDown}
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {normalizedAttendees.map((email, index) => (
                  <div key={index} className="flex items-center gap-2 px-2 py-1 border rounded-lg shadow-sm bg-zinc-300 border-zinc-300">
                    <BsPersonCircle className="w-4 h-4 text-zinc-600" />
                    <span className="text-xs font-medium text-zinc-600">{email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* --- Buttons --- */}
        <div className="flex flex-col items-end justify-end w-full gap-2 mt-2 sm:flex-row sm:items-center">
          {isEditable && (
            <>
              <button
                onClick={handleCancel}
                className="w-full px-3 py-1 text-sm text-white bg-gray-600 rounded-md sm:w-fit hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="w-full px-3 py-1 text-sm text-white bg-blue-600 rounded-md sm:w-fit hover:bg-blue-700"
              >
                Save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}